'use client';

import { useCallback, useState } from 'react';
import { Box, Typography, Paper, Theme } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useDropzone } from 'react-dropzone';

interface FileUploadProps {
  accept?: Record<string, string[]>;
  maxSize?: number;
  onFileSelect: (file: File) => void;
  helperText?: string;
}

export default function FileUpload({
  accept = {
    'model/stl': ['.stl'],
    'application/octet-stream': ['.stl'],
  },
  maxSize = 50 * 1024 * 1024, // 50MB default
  onFileSelect,
  helperText = 'Drag and drop your STL file here, or click to select',
}: FileUploadProps) {
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors[0].code === 'file-too-large') {
          setError(`File is too large. Maximum size is ${maxSize / 1024 / 1024}MB`);
        } else if (rejection.errors[0].code === 'file-invalid-type') {
          setError('Invalid file type. Please upload an STL file');
        } else {
          setError('Error uploading file');
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        setError(null);
        onFileSelect(acceptedFiles[0]);
      }
    },
    [maxSize, onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
  });

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        backgroundColor: (theme: Theme) =>
          isDragActive ? theme.palette.action.hover : theme.palette.background.paper,
      }}
    >
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: (theme: Theme) =>
            error ? 'error.main' : isDragActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon
          sx={{
            fontSize: 48,
            mb: 2,
            color: (theme: Theme) =>
              error ? theme.palette.error.main : theme.palette.text.secondary,
          }}
        />
        <Typography variant="body1" gutterBottom>
          {isDragActive ? 'Drop the file here' : helperText}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Maximum file size: {maxSize / 1024 / 1024}MB
        </Typography>
        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
